import React, { useState, useEffect, useMemo } from 'react';
import { Search, Navigation, Car, MapPin, Clock, Trash2, X, Activity, Gauge, Route, ChevronDown, ChevronRight, Users, Calendar } from 'lucide-react';
import axios from 'axios';

interface Trip {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    status: string;
    start_time?: string;
    end_time?: string;
    start_address?: string;
    end_address?: string;
    start_latitude?: number;
    start_longitude?: number;
    end_latitude?: number;
    end_longitude?: number;
    total_distance: number;
    duration: number;
    max_speed: number;
    avg_speed: number;
    activity_type?: string;
    created_at: string;
}

interface TripStats {
    total_trips: number;
    active_trips: number;
    completed_trips: number;
    period_trips: number;
    total_distance_km: number;
    avg_speed_kmh: number;
    by_activity: { type: string; count: number }[];
    by_status: { status: string; count: number }[];
}

// Stat Card
const StatCard: React.FC<{ icon: React.ReactNode; value: number | string; label: string; color: string }> = ({ icon, value, label, color }) => {
    const colors: Record<string, string> = {
        purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
        green: 'from-green-50 to-green-100 border-green-200 text-green-700',
        blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
        orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-700',
    };

    return (
        <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
            <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
            <p className="text-xl font-semibold">{value}</p>
            <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
        </div>
    );
};

const TripManagement: React.FC = () => {
    const [trips, setTrips] = useState<Trip[]>([]);
    const [stats, setStats] = useState<TripStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<string>('');
    const [selected, setSelected] = useState<Trip | null>(null);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        fetchTrips();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const fetchTrips = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter) params.append('status_filter', filter);
            params.append('limit', '5000');
            const url = `https://api.lifeshare.app/api/v1/admin/trips?${params.toString()}`;
            const r = await axios.get(url);
            setTrips(r.data.trips || []);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const fetchStats = async () => {
        try {
            const r = await axios.get('https://api.lifeshare.app/api/v1/admin/trips/stats?days=30');
            setStats(r.data);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu yolculuğu silmek istiyor musunuz?')) return;
        setActionLoading(id);
        try {
            await axios.delete(`https://api.lifeshare.app/api/v1/admin/trips/${id}`);
            setTrips(trips.filter(t => t.id !== id));
            if (selected?.id === id) setSelected(null);
        } catch (e) { alert('Silme işlemi başarısız'); }
        setActionLoading(null);
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-700';
            case 'completed': return 'bg-blue-100 text-blue-700';
            case 'cancelled': return 'bg-red-100 text-red-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const getActivityIcon = (type?: string) => {
        switch (type) {
            case 'automotive': return '🚗';
            case 'walking': return '🚶';
            case 'cycling': return '🚴';
            case 'running': return '🏃';
            default: return '📍';
        }
    };

    const formatDuration = (s: number) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
    };

    const formatDistance = (m: number) => m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;

    const filtered = trips.filter(t => {
        const matchesSearch =
            t.user_username.toLowerCase().includes(search.toLowerCase()) ||
            (t.start_address || '').toLowerCase().includes(search.toLowerCase()) ||
            (t.end_address || '').toLowerCase().includes(search.toLowerCase());

        let matchesDate = true;
        if (dateFrom) matchesDate = new Date(t.created_at) >= new Date(dateFrom + 'T00:00:00');
        if (dateTo && matchesDate) matchesDate = new Date(t.created_at) <= new Date(dateTo + 'T23:59:59');

        return matchesSearch && matchesDate;
    });

    const groupedTrips = useMemo(() => {
        const groups: Record<number, Trip[]> = {};
        for (const t of filtered) {
            if (!groups[t.user_id]) groups[t.user_id] = [];
            groups[t.user_id].push(t);
        }
        const sortedKeys = Object.keys(groups).map(Number).sort((a, b) => {
            const aTime = new Date(groups[a][0].created_at).getTime();
            const bTime = new Date(groups[b][0].created_at).getTime();
            return bTime - aTime;
        });
        return { groups, sortedKeys };
    }, [filtered]);

    const toggleGroup = (userId: number) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const formatRelativeTime = (dateStr: string) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
        if (diffMin < 1) return 'Az önce';
        if (diffMin < 60) return `${diffMin} dk önce`;
        const diffHr = Math.floor(diffMin / 60);
        if (diffHr < 24) return `${diffHr} saat önce`;
        const diffDay = Math.floor(diffHr / 24);
        if (diffDay === 1) return 'Dün';
        if (diffDay < 7) return `${diffDay} gün önce`;
        return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Yolculuklar</h1>
                    <p className="text-sm text-gray-500 mt-1">{trips.length} yolculuk</p>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <StatCard icon={<Navigation />} value={stats.total_trips} label="Toplam" color="purple" />
                        <StatCard icon={<Activity />} value={stats.active_trips} label="Aktif" color="green" />
                        <StatCard icon={<Route />} value={`${stats.total_distance_km} km`} label="Mesafe" color="blue" />
                        <StatCard icon={<Gauge />} value={`${stats.avg_speed_kmh}`} label="Ort. Hız" color="orange" />
                    </div>
                )}

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl p-4 mb-6">
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Kullanıcı veya adres ara..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div className="flex gap-2 flex-wrap mb-3">
                        {[
                            { value: '', label: 'Tümü' },
                            { value: 'active', label: 'Aktif' },
                            { value: 'completed', label: 'Tamamlandı' },
                            { value: 'cancelled', label: 'İptal' },
                        ].map(f => (
                            <button
                                key={f.value}
                                onClick={() => setFilter(f.value)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.value ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                {f.label}
                            </button>
                        ))}
                        <span className="ml-auto text-xs text-gray-500 self-center">{filtered.length} sonuç</span>
                    </div>

                    {/* Date Filter */}
                    <div className="flex flex-col sm:flex-row gap-3 items-end">
                        <div className="flex-1 w-full">
                            <label className="text-xs text-gray-500 mb-1 block">Başlangıç</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <div className="flex-1 w-full">
                            <label className="text-xs text-gray-500 mb-1 block">Bitiş</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <button
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                            className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                        >
                            Temizle
                        </button>
                    </div>
                </div>

                {/* Trips — Grouped by User */}
                {filtered.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Navigation className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Yolculuk bulunamadı</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {groupedTrips.sortedKeys.map((userId) => {
                            const userTrips = groupedTrips.groups[userId];
                            const firstTrip = userTrips[0];
                            const isExpanded = expandedGroups.has(userId);
                            const totalDistance = userTrips.reduce((sum, t) => sum + t.total_distance, 0);
                            const activeCount = userTrips.filter(t => t.status === 'active').length;
                            const latestTime = userTrips[0].created_at;
                            const visibleTrips = userTrips;

                            return (
                                <div key={userId} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                                    {/* Group Header */}
                                    <button
                                        onClick={() => toggleGroup(userId)}
                                        className="w-full flex items-center gap-3 p-4 bg-gray-50/50 hover:bg-gray-50 transition-all"
                                    >
                                        {firstTrip.user_avatar ? (
                                            <img src={`https://api.lifeshare.app${firstTrip.user_avatar}`} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                                        ) : (
                                            <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                                <span className="text-xl">{getActivityIcon(firstTrip.activity_type)}</span>
                                            </div>
                                        )}
                                        <div className="flex-1 text-left">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-900 text-sm">{firstTrip.user_name || firstTrip.user_username}</span>
                                                <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                                                    {userTrips.length} yolculuk
                                                </span>
                                                {activeCount > 0 && (
                                                    <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium border border-green-200">
                                                        {activeCount} aktif
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Car className="w-3 h-3" />
                                                    {formatDistance(totalDistance)} toplam
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    En son: {formatRelativeTime(latestTime)}
                                                </span>
                                            </div>
                                        </div>
                                        {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                                    </button>

                                    {/* Group Items */}
                                    {isExpanded && (
                                        <div className="divide-y divide-gray-50">
                                            {visibleTrips.map(t => (
                                                <div key={t.id} className="p-4 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => setSelected(t)}>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <span className="text-lg flex-shrink-0">{getActivityIcon(t.activity_type)}</span>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs text-gray-500">#{t.id} • {new Date(t.created_at).toLocaleDateString('tr-TR')}</p>
                                                        </div>
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusStyle(t.status)}`}>
                                                            {t.status === 'active' ? 'Aktif' : t.status === 'completed' ? 'Bitti' : 'İptal'}
                                                        </span>
                                                    </div>

                                                    {/* Route */}
                                                    <div className="relative pl-5 mb-2">
                                                        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-green-400 to-red-400" />
                                                        <div className="relative mb-2">
                                                            <div className="absolute -left-5 top-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow" />
                                                            <p className="text-xs text-gray-600 truncate">{t.start_address || 'Başlangıç noktası'}</p>
                                                        </div>
                                                        <div className="relative">
                                                            <div className="absolute -left-5 top-0.5 w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow" />
                                                            <p className="text-xs text-gray-600 truncate">{t.end_address || 'Varış noktası'}</p>
                                                        </div>
                                                    </div>

                                                    {/* Stats */}
                                                    <div className="flex items-center justify-between pt-2 border-t border-gray-50 text-[11px] text-gray-500">
                                                        <div className="flex items-center gap-3">
                                                            <span className="flex items-center gap-1"><Car className="w-3 h-3" />{formatDistance(t.total_distance)}</span>
                                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(t.duration)}</span>
                                                        </div>
                                                        {t.avg_speed > 0 && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{t.avg_speed.toFixed(0)} km/s</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>

            {/* Modal */}
            {selected && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={() => setSelected(null)}>
                    <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl max-h-[92vh] overflow-auto rounded-t-3xl" onClick={e => e.stopPropagation()}>

                        {/* Handle */}
                        <div className="sm:hidden flex justify-center pt-2 pb-1">
                            <div className="w-9 h-1 bg-gray-300 rounded-full" />
                        </div>

                        {/* Header with gradient */}
                        <div className="px-5 py-5 bg-gradient-to-br from-purple-50 to-purple-100/50 border-b border-purple-100">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    {selected.user_avatar ? (
                                        <img src={`https://api.lifeshare.app${selected.user_avatar}`} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm" />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center ring-2 ring-purple-100 shadow-sm">
                                            <span className="text-2xl">{getActivityIcon(selected.activity_type)}</span>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-gray-900 text-base">{selected.user_name || selected.user_username}</p>
                                        <p className="text-xs text-gray-500">@{selected.user_username}</p>
                                    </div>
                                </div>
                                <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors">
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(selected.status)}`}>
                                    {selected.status === 'active' ? '● Aktif' : selected.status === 'completed' ? '✓ Tamamlandı' : '✕ İptal'}
                                </span>
                                <span className="px-3 py-1 bg-white/70 text-gray-600 rounded-full text-xs font-medium">
                                    {getActivityIcon(selected.activity_type)} {selected.activity_type || 'Bilinmiyor'}
                                </span>
                                <span className="px-3 py-1 bg-white/70 text-gray-600 rounded-full text-xs font-medium">
                                    #{selected.id}
                                </span>
                            </div>
                        </div>

                        {/* Stats Cards */}
                        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100/30 border border-purple-100 rounded-2xl p-3 text-center">
                                <Route className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                                <p className="text-base font-bold text-purple-800">{formatDistance(selected.total_distance)}</p>
                                <p className="text-[10px] text-purple-500 uppercase tracking-wide">Mesafe</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100/30 border border-green-100 rounded-2xl p-3 text-center">
                                <Clock className="w-4 h-4 text-green-400 mx-auto mb-1" />
                                <p className="text-base font-bold text-green-800">{formatDuration(selected.duration)}</p>
                                <p className="text-[10px] text-green-500 uppercase tracking-wide">Süre</p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100/30 border border-blue-100 rounded-2xl p-3 text-center">
                                <Gauge className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                                <p className="text-base font-bold text-blue-800">{selected.avg_speed.toFixed(1)}</p>
                                <p className="text-[10px] text-blue-500 uppercase tracking-wide">Ort. km/s</p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100/30 border border-orange-100 rounded-2xl p-3 text-center">
                                <Activity className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                                <p className="text-base font-bold text-orange-800">{selected.max_speed.toFixed(1)}</p>
                                <p className="text-[10px] text-orange-500 uppercase tracking-wide">Max km/s</p>
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="px-5 pb-4">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Rota Detayı</p>
                            <div className="relative pl-6">
                                {/* Vertical line */}
                                <div className="absolute left-[10px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-green-400 via-gray-200 to-red-400" />

                                {/* Start */}
                                <div className="relative mb-4">
                                    <div className="absolute -left-6 top-0.5 w-5 h-5 rounded-full bg-green-500 border-[3px] border-white shadow-md flex items-center justify-center">
                                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                    </div>
                                    <div className="ml-1">
                                        <p className="text-[10px] font-medium text-green-600 uppercase tracking-wide mb-0.5">Başlangıç</p>
                                        <p className="text-sm text-gray-900 leading-snug">{selected.start_address || 'Başlangıç noktası'}</p>
                                        {selected.start_time && (
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {new Date(selected.start_time).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        )}
                                        {selected.start_latitude && selected.start_longitude && (
                                            <a
                                                href={`https://maps.apple.com/?ll=${selected.start_latitude},${selected.start_longitude}&q=Baslangic`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-1"
                                            >
                                                <MapPin className="w-3 h-3" /> Haritada Aç
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {/* End */}
                                <div className="relative">
                                    <div className="absolute -left-6 top-0.5 w-5 h-5 rounded-full bg-red-500 border-[3px] border-white shadow-md flex items-center justify-center">
                                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                    </div>
                                    <div className="ml-1">
                                        <p className="text-[10px] font-medium text-red-600 uppercase tracking-wide mb-0.5">Varış</p>
                                        <p className="text-sm text-gray-900 leading-snug">{selected.end_address || 'Varış noktası'}</p>
                                        {selected.end_time && (
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {new Date(selected.end_time).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        )}
                                        {selected.end_latitude && selected.end_longitude && (
                                            <a
                                                href={`https://maps.apple.com/?ll=${selected.end_latitude},${selected.end_longitude}&q=Varis`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-1"
                                            >
                                                <MapPin className="w-3 h-3" /> Haritada Aç
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Speed Analysis */}
                        {selected.avg_speed > 0 && (
                            <div className="px-5 pb-4">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Hız Analizi</p>
                                <div className="bg-gray-50 rounded-2xl p-3 space-y-2">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-500">Ortalama Hız</span>
                                        <span className="font-semibold text-gray-800">{selected.avg_speed.toFixed(1)} km/s</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-500">Maksimum Hız</span>
                                        <span className="font-semibold text-gray-800">{selected.max_speed.toFixed(1)} km/s</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-500">Hız Farkı</span>
                                        <span className="font-semibold text-gray-800">+{(selected.max_speed - selected.avg_speed).toFixed(1)} km/s</span>
                                    </div>
                                    {/* Speed bar */}
                                    <div className="pt-1">
                                        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-orange-400 rounded-full"
                                                style={{ width: `${Math.min((selected.avg_speed / Math.max(selected.max_speed, 1)) * 100, 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-[9px] text-gray-400 mt-1">
                                            <span>0</span>
                                            <span>Max: {selected.max_speed.toFixed(0)} km/s</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Trip Info */}
                        <div className="px-5 pb-4">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Yolculuk Bilgisi</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-3 bg-gray-50 rounded-xl">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Oluşturma</p>
                                    <p className="text-sm text-gray-800">{new Date(selected.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-xl">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Saat</p>
                                    <p className="text-sm text-gray-800">{new Date(selected.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-5 pb-5 pt-3 border-t border-gray-100">
                            <button
                                onClick={() => handleDelete(selected.id)}
                                disabled={actionLoading === selected.id}
                                className="w-full py-3 bg-red-50 text-red-600 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
                            >
                                {actionLoading === selected.id ? (
                                    <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                Yolculuğu Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TripManagement;
